/* eslint-disable @typescript-eslint/explicit-function-return-type */
import axios from "axios";
/* eslint-disable import/no-named-as-default-member */
import queryString from "query-string";
import * as amplitude from "@amplitude/analytics-browser";

import { sensitiveEnv } from "../../config/sensitiveEnv";
import { AxiosInstance } from "../../shared/utils/axios";
import { getCookieBotConsentStatistics } from "../../shared/utils/cookieConsent";
import i18n from "../../i18n";
import type { MeResponse } from "../../shared/utils/BaseClient";
import { resetUserAttributesInDatadogRUM } from "../../shared/utils/datadog";

const OAUTH_CLIENT_ID = sensitiveEnv.oauthClientId;
const OAUTH_SECRET = sensitiveEnv.oauthSecret;

const O_AUTH = {
  responseCode: "code",
  clientId: OAUTH_CLIENT_ID,
};

const ACCOUNTS_ENDPOINT = process.env.REACT_APP_ACCOUNTS_ENDPOINT ?? "";

const API_URL = process.env.REACT_APP_BASE_API_URL ?? "";
const ORG_ID = process.env.REACT_APP_ORG_ID ?? "";
const WORKSPACE_ID = process.env.REACT_APP_ORG_ID ?? "";

const CLIENTIDAUTHCODE_ENC = btoa(`${OAUTH_CLIENT_ID}:${OAUTH_SECRET}`);

const AUTH_HEADERS = {
  "Content-Type": "application/x-www-form-urlencoded",
  authorization: `Basic ${CLIENTIDAUTHCODE_ENC}`,
};

const clearAccessToken = (): void => {
  localStorage.removeItem("tokens");
  sessionStorage.removeItem("tokens");
};

export const logout = async () => {
  resetUserAttributesInDatadogRUM();
  clearAccessToken();
  window.location.href = `https://${ACCOUNTS_ENDPOINT}/exit?continue=${window.location.origin}`;
  return "Time for a nap. Logging out..";
};

const isAccessTokenAvailable = () => localStorage.getItem("tokens") !== null;

/**
 * to get the name and initials from the given me response
 * @param me
 * @returns
 */
export const determineName = (me: MeResponse) => {
  const fullName =
    me.first_name && me.last_name
      ? `${me.first_name} ${me.last_name}`
      : undefined;

  return fullName ?? me.name ?? me.email;
};

/**
 * to update the amplitude user properties with the given name and me response
 * @param name
 * @param me
 * @returns
 */
const setAmplitudeUserProperties = (me: MeResponse) => {
  if (!getCookieBotConsentStatistics()) {
    return;
  }
  const updatedIdentity = new amplitude.Identify();
  const name = determineName(me);
  const language = i18n.language;
  updatedIdentity.set("name", name);
  updatedIdentity.set("email", me.email);
  updatedIdentity.set("role", me.role);
  updatedIdentity.set("user_type", me.type);
  updatedIdentity.set("language", language);
  updatedIdentity.set(
    "screen_resolution",
    `${globalThis.screen.width}*${globalThis.screen.height}`
  );
  updatedIdentity.set("country_code", me.country_code ?? "UNKNOWN");
  updatedIdentity.set("job_title", me.job_title ?? "UNKNOWN");
  amplitude.identify(updatedIdentity);
};

export const authService = (function () {
  const getOAuthUrl = (): string => {
    return `https://${ACCOUNTS_ENDPOINT}/oauth/authorize?response_type=${O_AUTH.responseCode}&client_id=${O_AUTH.clientId}&redirect_uri=${window.location.origin}`;
  };

  const performLogin = async (code: any) => {
    const authParams = {
      grant_type: "authorization_code",
      client_id: O_AUTH.clientId,
      redirect_uri: `${window.location.origin}`,
      code,
    };
    try {
      const res = await axios.post(
        `${API_URL}/oauth/token`,
        queryString.stringify(authParams as any),
        { headers: AUTH_HEADERS }
      );
      const tokens = res.data;
      localStorage.setItem("tokens", JSON.stringify(tokens));
      sessionStorage.setItem("tokens", JSON.stringify(tokens));
      // await AxiosInstance.get(
      //   `${API_URL}/v2/licensing/status?workspace=${WORKSPACE_ID}&org=${ORG_ID}`,
      //   { headers: AUTH_HEADERS }
      // );
      console.warn(ORG_ID, WORKSPACE_ID);
      return tokens;
    } catch (e) {
      console.error("e", e);
      await logout();
    }
  };

  const currentUser = async () => {
    try {
      const res = await AxiosInstance.get("/v2/accounts/me", {
        baseURL: API_URL,
      });
      setAmplitudeUserProperties(res.data);
      return res.data;
    } catch (e) {
      await logout();
    }
  };

  return {
    performLogin,
    currentUser,
    isAccessTokenAvailable,
    getOAuthUrl,
    logout,
  };
})();
